import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bike, Megaphone, X } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";

export const BAR_COLORS = {
  red: "bg-[#E04F33] text-white",
  green: "bg-[#1F7A4D] text-white",
  gold: "bg-[#B97A1F] text-white",
  black: "bg-[#0F0F0F] text-white",
};
const DISMISS_KEY = "urban_announce_dismissed";
const DAY = 24 * 60 * 60 * 1000;

const readDismiss = () => { try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || "null"); } catch { return null; } };

// Presentational bar (also used for the Back Office live preview).
export function AnnouncementBanner({ data, onDismiss, onNavigate, preview = false }) {
  if (!data?.active) return null;
  const Icon = data.link_type === "delivery" ? Bike : Megaphone;
  const cls = `announce-bar ${data.animation === "still" ? "" : "announce-pulse"} ${BAR_COLORS[data.color] || BAR_COLORS.red} relative overflow-hidden rounded-2xl shadow-sm`;
  const inner = (
    <span className="flex items-start sm:items-center gap-2.5 min-w-0 pr-8 sm:pr-10">
      <Icon size={18} className="shrink-0 mt-0.5 sm:mt-0" aria-hidden="true" />
      <span className="font-bold text-sm sm:text-base leading-snug line-clamp-2" data-testid="announcement-text">{data.message}</span>
      {data.href && <ArrowRight size={18} className="shrink-0 hidden sm:inline" aria-hidden="true" />}
    </span>
  );
  const external = data.href && /^https?:\/\//.test(data.href);
  return (
    <div className={cls} role="region" aria-label="Announcement" data-testid={preview ? "announcement-preview" : "announcement-bar"}>
      {data.href && !preview ? (
        external
          ? <a href={data.href} target="_blank" rel="noopener noreferrer" className="block px-4 py-3 sm:px-5" data-testid="announcement-link">{inner}</a>
          : <Link to={data.href} onClick={onNavigate} className="block px-4 py-3 sm:px-5" data-testid="announcement-link">{inner}</Link>
      ) : <div className="px-4 py-3 sm:px-5">{inner}</div>}
      <button type="button" onClick={onDismiss} aria-label="Dismiss announcement" data-testid="announcement-dismiss"
        className="absolute top-1/2 -translate-y-1/2 right-2.5 h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-white/20 transition-colors">
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );
}

// Home-page announcement: fetched from /api/announcement (60s cache), dismissible for 24h per visitor.
export function AnnouncementBar() {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const { setChannel } = useCart();

  useEffect(() => {
    api.get("/announcement").then(({ data: d }) => {
      setData(d);
      const dis = readDismiss();
      if (dis && dis.version === d.version && dis.until > Date.now()) setHidden(true);
    }).catch(() => setData(null));
  }, []);

  const dismiss = () => {
    setHidden(true);
    try { localStorage.setItem(DISMISS_KEY, JSON.stringify({ version: data?.version, until: Date.now() + DAY })); } catch { /* private mode */ }
  };
  const onNavigate = () => {
    if (data?.link_type === "delivery") setChannel("delivery");
    if (data?.link_type === "pickup") setChannel("pickup");
  };
  if (!data?.active || hidden) return null;
  return (
    <div className="pt-4" data-testid="announcement-slot">
      <AnnouncementBanner data={data} onDismiss={dismiss} onNavigate={onNavigate} />
    </div>
  );
}
